<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    Spinner,
    LabelDropdown,
    NextButton,
  },
  setup() {
    const { isAdmin } = useAdmin();

    const { activeLabels, accountLabels, onUpdateLabels } =
      useConversationLabels();

    const showSearchDropdownLabel = ref(false);

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      showSearchDropdownLabel.value = false;
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);
    return {
      isAdmin,
      activeLabels,
      accountLabels,
      onUpdateLabels,
      showSearchDropdownLabel,
      closeDropdownLabel,
      toggleLabels,
    };
  },
  computed: {
    ...mapGetters({
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
    selectedLabel() {
      return this.activeLabels[0] || null;
    },
    selectedLabelTitles() {
      return this.selectedLabel ? [this.selectedLabel.title] : [];
    },
    selectedBorderColor() {
      return this.selectedLabel?.color || null;
    },
    selectStyle() {
      if (!this.selectedBorderColor) {
        return {};
      }

      return {
        outlineColor: this.selectedBorderColor,
      };
    },
    selectedLabelsText() {
      if (!this.selectedLabel) {
        return this.$t('CONTACT_PANEL.LABELS.CONVERSATION.ADD_BUTTON');
      }

      return this.selectedLabel.title;
    },
  },
  methods: {
    handleAddLabel(label) {
      this.onUpdateLabels([label.title]);
      this.closeDropdownLabel();
    },
    handleRemoveLabel() {
      this.onUpdateLabels([]);
      this.closeDropdownLabel();
    },
  },
};
</script>

<template>
  <div class="relative z-[100] flex-shrink-0">
    <div v-if="!conversationUiFlags.isFetching" class="relative">
      <div
        v-on-clickaway="closeDropdownLabel"
        class="relative"
        @keyup.esc="closeDropdownLabel"
      >
        <NextButton
          :label="selectedLabelsText"
          icon="i-lucide-chevron-down"
          trailing-icon
          size="sm"
          color="slate"
          no-animation
          class="h-8 max-w-[9rem] !rounded-lg shadow outline-1 outline outline-n-container"
          :style="selectStyle"
          @click="toggleLabels"
        />

        <div
          :class="{
            'block visible': showSearchDropdownLabel,
            'hidden invisible': !showSearchDropdownLabel,
          }"
          class="absolute z-[99999] w-56 p-2 mt-1 border rounded-2xl shadow-2xl top-full ltr:left-0 rtl:right-0 border-n-weak bg-white box-border"
        >
          <LabelDropdown
            v-if="showSearchDropdownLabel"
            :account-labels="accountLabels"
            :selected-labels="selectedLabelTitles"
            :allow-creation="isAdmin"
            @add="handleAddLabel"
            @remove="handleRemoveLabel"
          />
        </div>
      </div>
    </div>
    <Spinner v-else size="tiny" />
  </div>
</template>
